import React, { useEffect, useState } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../actions";
import { ResponsiveBox, Typography } from "../primitives";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import CardList from "../custom/containers/cardList";
import { userActions } from "../../actions/user.actions";
import { transactionSearchHeading } from "../../constants/TransactionSearchConstants.js";
import moment from "moment";
import { RoleType }  from "../../constants/sharedConstants.js";
import ERPSRecallRejectSearch, { getDateLimits, billerDetails } from "../custom/ERPSRecallRejectSearch";
import { listProps,propValue, getComponentAccess, formatDate, setFinancialInstitutionUIData } from "../../utils/helper";

const ReviewTransactions = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    history,
    location: { state },
  } = props;

  const [loading, setLoading] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  const [portalType] = useState(localStorage.getItem("type"));
  const user = useSelector((state) => state.loginUserReducer.user);
  const [isBillerRole] = useState(user.role.role_type === RoleType.BILLER_ADMIN.type || user.role.role_type === RoleType.BILLER_USER.type);
  const [isAdminRole] = useState(user.role.role_type === RoleType.ERPADMIN.type || user.role.role_type === RoleType.CEADMIN.type || user.role.role_type === RoleType.BILLADMIN.type );
  const [isSubscription] = useState([RoleType.FI_ADMIN.type, RoleType.FI_OPERATOR.type].includes(user.role.role_type));
  const currentFiNumber = useSelector((state) => state.fiReducer?.selectedFi || user?.fiNumber);

  const componentAccess = state?.componentAccess
          || getComponentAccess( { function_type_code: "RT", label_type_code: "TE" }, user );

  let dates = getDateLimits("RT", isAdminRole);
  
  const initialSearchValues = {
    archive: "n",
    archiveMonth: dates.archiveMonth,
    subscribeEntityId: "",
    paymentFromDate: dates.aMonthAgo,
    paymentToDate: dates.today,
    transactionToAmount: "",
    transactionFromAmount: "",
    verificationNumber: "",
    selectedFiNumber: "0",
    searchBy: billerDetails[0].value,   // "billerId"
    billerId: isBillerRole ? user.billerNonVolatileDto.biller_id : null, // internal biller id for Biller Role
    bmoBillerId: "",  // external biller id entered by the user
    billerName: "",
    billerCustomerNumber: "",
    fiCustomerName: "",
    fiCustomerNumber: "",
    sortBy: "BC"
  };

  const rtResponse = useSelector( (state) => state.transactionEnquiryReducer );

  const [searchValues, setSearchValues] = useState(initialSearchValues);
  const [actualCriteria, setActualCriteria] = useState(
    state?.fromDetailPage ? rtResponse?.transactionEnquiryDetails : { ...initialSearchValues, offset: 0, limit: 100}
  );

  const [transactionData, setTransactionData] = useState( state?.fromDetailPage && rtResponse?.transactionEnquiryData?.data || null );

  const subscribeEntities = useSelector( (state) => state.subscribingEntitiesReducer );
  const [subscribeEntityList, setSubscribeEntityList] = useState([]);

  const [fiList, setFiList] = useState([]);
  const [fiMap, setFiMap] = useState(null);
  const fiReducer = useSelector( (state) => state.fiReducer );

  useEffect(() => {
    isBillerRole && setFinancialInstitutionUIData(fiReducer?.filteredFinancialInstitutionList, setFiMap, setFiList, null, user?.languageCode);
  }, [fiReducer.filteredFinancialInstitutionList, user?.languageCode]);

  useEffect(() => {
    (isAdminRole && currentFiNumber === '50') && setFinancialInstitutionUIData(fiReducer?.fiListByOwningFi, setFiMap, setFiList, null, user?.languageCode, "erps.common.label.allBMOFis");
  }, [fiReducer.fiListByOwningFi, user?.languageCode]);

  useEffect(() => {
    if (isSubscription && subscribeEntities.subscribingEntitiesList?.length > 0) {
      const data = subscribeEntities.subscribingEntitiesList.map( se => ({value: se.fiCompanyNumber, label: se.fiCompanyName}) );
      data.unshift({value: "", label: ""});
      setSubscribeEntityList(data);
    }
  }, [subscribeEntities.subscribingEntitiesList]);

  useEffect(() => {
    if (isBillerRole && !(fiReducer?.filteredFinancialInstitutionList?.length > 0))
      dispatch(actions.fiActions.getFilteredFinancialInstitutionList());

    if (isAdminRole && currentFiNumber === '50' && !(fiReducer?.fiListByOwningFi?.length > 0))
      dispatch(actions.fiActions.getFinancialInstitutionListByOwningFiNumber(t));

    if (isSubscription && !(subscribeEntities.subscribingEntitiesList?.length > 0))
      dispatch(userActions.getSubscribingEntitiesList(currentFiNumber));

    if (state?.fromDetailPage) {
      // transfer into searchValues the attributes saved in rejectTransactionResponse.transactionForRejectDetails
      let newSearchValues = {}
      Object.keys(searchValues).filter(key => rtResponse.transactionEnquiryDetails[key])
            .forEach(key => newSearchValues[key] = rtResponse?.transactionEnquiryDetails[key]);
      setSearchValues(newSearchValues);
      setPageNumber(actualCriteria.offset / actualCriteria.limit);
      state.fromDetailPage = null;  // resetting the coming-from-detail-page flag
    } else {
      resetSearchDataInReducer();
    }
  }, []);

  function resetSearchDataInReducer() { // reset search resutls previously saved in the reducer
    dispatch({
      type: "GET_TRANSACTION_ENQUIRY_SUCCESS",
      data: { data: null, transactionEnquiryDetails: null },
    });
  }

  useEffect(() => {
    if (loading === false && rtResponse) {
      const responseData = rtResponse.transactionEnquiryData?.data;
      if (responseData?.length)
        responseData.forEach((r, idx) => r.seqNo = idx + 1 + actualCriteria.offset);
      setTransactionData(responseData);
    }
  }, [loading]);

  const paginate = (values) => {  // 'values' has fields startOffset and endOffset (e.g., for 1st Page: 0,25;  2nd Page: 25,50;  3rd: 50,75...)
    actualCriteria.offset = values.startOffset;
    if (actualCriteria.limit !== (values.endOffset - values.startOffset)) // if the user changed the number of rows per page
      actualCriteria.limit = values.endOffset - values.startOffset;

    loadResults(actualCriteria);
  };

  const loadResults = (queryParams) => {
    if (errorMessage)
      setErrorMessage(null);
    setLoading(true);

    dispatch(
      actions.transactionEnquiryActions.getTransactionEnquiry(
        queryParams,
        { ...props, t, history, setLoading, setErrorMessage }
      )
    );
  };

  const handleReset = () => {
    setSearchValues({...initialSearchValues});
    setActualCriteria({ ...initialSearchValues, offset: 0, limit: actualCriteria.limit});
    setPageNumber(0);
    setErrorMessage(null);
    setTransactionData([]);

    resetSearchDataInReducer();
  };

  const handleSearch = (groomedValues) => {
    if (pageNumber)
      setPageNumber(0);

    if (groomedValues.paymentFromDate)
      groomedValues.paymentFromDate = formatDate(moment(groomedValues.paymentFromDate).format());
    if (groomedValues.paymentToDate)
      groomedValues.paymentToDate = formatDate(moment(groomedValues.paymentToDate).format());
    groomedValues.limit = actualCriteria.limit;
    groomedValues.offset = 0;
    // groomedValues.currentFiNumber = user?.fiNumber;

    // //NOT SURE I NEED THIS:
    // groomedValues.selectedFiNumber = selectedFiNumber;

    if (!isBillerRole) {
      if (groomedValues.searchBy === billerDetails[0].value)  // "billerId"
        delete groomedValues["billerName"];
      else if (groomedValues.searchBy === billerDetails[1].value)  //  "billerName"
        delete groomedValues["bmoBillerId"];
    }
    //?? Do I even need this?? //groomedValues.billerId = groomedValues.bmoBillerId; // Because PCE search uses .billerId property for external biller id
    setActualCriteria({...groomedValues});

    loadResults(groomedValues);
  };

  function isShowFi() {
    return portalType === "billpay" && isBillerRole;
  }

  return (
    <div role="main">
      <ResponsiveBox ml={3}>
        <ResponsiveBox sx={{ width: { xs: "100%", lg: "100%" }, maxWidth: "100%" }}>
          <ERPSRecallRejectSearch 
              pageType="RT"
              pageTitle={t("erps.reviewTransactions.page.title")}
              pageDescription={t("erps.transactionEnquiry.page.description")}
              searchValues={searchValues}
              setSearchValues={setSearchValues}
              dates={dates}
              loading={loading}
              errorMessage={errorMessage}
              componentAccess={componentAccess}
              handleReset={handleReset}
              handleSearch={handleSearch}
              financialInstitutionList={fiList}
              subscribeEntityList={isSubscription ? subscribeEntityList : null}
            />

          {transactionData !== null && transactionData !== undefined
           && Array.isArray(transactionData) && transactionData.length > 0 ? (
            <>
              <ResponsiveBox className="manageUsers_table_visible">
                <ResponsiveDataGrid
                  data={{
                    rows: transactionData,
                    columns: transactionSearchHeading("RT", isShowFi() ? fiMap : null, "/review-transactions/transaction-details", currentFiNumber, user),
                    count: rtResponse?.transactionEnquiryData?.count || 0,
                  }}
                  sumOfColumnWidth="true"
                  onAnyPaginationAction={paginate}
                  initialLoading={loading}
                  pageNumber={pageNumber}
                  setPage={setPageNumber}
                  rowPerPage={actualCriteria.limit}
                  tableLabelText={"_Page Title_"}
                  hideFooterPagination={false}
                  pageName={"transaction-enquiry"}
                />
              </ResponsiveBox>
              <ResponsiveBox className="manageUsers_card_visible">
                <CardList
                  data={{
                    rows: transactionData,
                    columns: transactionSearchHeading("RT", isShowFi() ? fiMap : null, "/review-transactions/transaction-details", currentFiNumber, user),
                    count: rtResponse?.transactionEnquiryData?.count || 0,
                  }}
                  onAnyPaginationAction={paginate}
                  hideFooterPagination={false}
                  pageName={"transaction-enquiry"}
                  pageNumber={pageNumber}
                  setPage={setPageNumber}
                  rowPerPage={actualCriteria.limit}
                />
              </ResponsiveBox>
            </>
          ) : (
            <ResponsiveBox mt={1}>
              <Typography variant="body1">
                  { (transactionData?.length === 0)
                      ? (`0 ${t("erps.transactionEnquiry.page.resultfound")}`)
                      : (t("erps.availabledocuments.searchuser.notstarted", "Search not started"))
                  }
              </Typography>
            </ResponsiveBox>
          )}
        </ResponsiveBox>
      </ResponsiveBox>
    </div>
  );
};

export default withSidebar(ReviewTransactions);
